import React, { useState } from 'react';
import { AgFinancingDocumentType, AgFinancingFile } from '@indigo-ag/schema';
import { Checkbox, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { utils } from 'shared';
import { updateAgFinancingFile } from 'features/brazil-grower/queries';

type Phase3FilesToValidate =
  | AgFinancingDocumentType.REGISTERED_MINUTA
  | AgFinancingDocumentType.REGISTERED_LETTER_OF_CONSENT
  | AgFinancingDocumentType.OTHER_VALIDATION
  | AgFinancingDocumentType.AMENDMENT_SIGN;

export const Phase3FilesToValidateKeys: { [key in Phase3FilesToValidate]: string } = {
  [AgFinancingDocumentType.REGISTERED_MINUTA]: 'isRegisteredMinutaValid',
  [AgFinancingDocumentType.REGISTERED_LETTER_OF_CONSENT]: 'isConsentLetterValid',
  [AgFinancingDocumentType.OTHER_VALIDATION]: 'isOtherValidationValid',
  [AgFinancingDocumentType.AMENDMENT_SIGN]: 'isAmendmentSignedValid'
};

interface ValidateDocumentProps {
  file: AgFinancingFile;
  readonly?: boolean;
}

export const ValidateDocument: React.FunctionComponent<ValidateDocumentProps> = ({ file, readonly = false }) => {
  const fileToValidateKey: string = Phase3FilesToValidateKeys[file.documentType];

  /**
   * State
   */
  const [isValid, setIsValid] = useState<boolean>(
    file.metadata?.phase3FileInfo ? !!file.metadata.phase3FileInfo[fileToValidateKey] : false
  );

  /**
   * Mutations
   */
  const [updateAgFinancingFileFn] = useMutation(updateAgFinancingFile);

  const { addFlashMessage } = useFlashMessage();

  const updateValid = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedValid = event.target.checked;

    setIsValid(updatedValid);

    const updatedDocument = [
      {
        id: file.id,
        metadata: {
          ...file.metadata,
          phase3FileInfo: {
            ...(file.metadata?.phase3FileInfo || {}),
            [fileToValidateKey]: updatedValid
          }
        }
      }
    ];
    updateAgFinancingFileFn({
      variables: {
        data: updatedDocument
      }
    })
      .then(() => {
        const successMsgKey = updatedValid
          ? 'documentMarkedAsValidSuccessfully'
          : 'documentMarkedAsInvalidSuccessfully';
        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t(successMsgKey)),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      });
  };

  return (
    <Checkbox
      checked={isValid}
      onChange={updateValid}
      disabled={readonly}
      label={utils.text.toTitleCase(i18next.t('valid'))}
      margin="dense"
    />
  );
};
